import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { HERO_SLIDES } from './heroSlidesData'

const TOTAL = HERO_SLIDES.length
const INTERVAL = 5000

function ServiceIcon({ path }) {
  return (
    <svg className="w-4.25 h-4.25 text-goldPrimary" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  )
}

/**
 * A hero CTA that routes with react-router's <Link> for in-app targets
 * (cta.internal) and a plain <a> for cross-app absolute URLs (e.g. the
 * jyotish/shop apps), driven entirely by the slide data's `internal` flag.
 */
function CtaLink({ cta, className, children }) {
  if (cta.internal) {
    return (
      <Link to={cta.to} className={className}>
        {children}
      </Link>
    )
  }
  return (
    <a href={cta.to} className={className}>
      {children}
    </a>
  )
}

export default function HeroSlider() {
  const [current, setCurrent] = useState(0)
  const trackRef = useRef(null)
  const timerRef = useRef(null)
  const touchStartXRef = useRef(0)

  const stop = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const start = useCallback(() => {
    stop()
    timerRef.current = window.setInterval(() => {
      setCurrent((c) => (c + 1) % TOTAL)
    }, INTERVAL)
  }, [stop])

  useEffect(() => {
    start()
    return stop
  }, [start, stop])

  const goTo = (n) => setCurrent(((n % TOTAL) + TOTAL) % TOTAL)

  const handleMouseDown = (e) => {
    const startCurrent = current
    const startX = e.clientX
    let dragDelta = 0
    if (trackRef.current) trackRef.current.style.transition = 'none'

    const onMove = (ev) => {
      dragDelta = ev.clientX - startX
      if (trackRef.current) {
        trackRef.current.style.transform = `translateX(calc(-${startCurrent * 100}% + ${dragDelta}px))`
      }
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      if (trackRef.current) trackRef.current.style.transition = ''
      if (Math.abs(dragDelta) > 60) {
        stop()
        goTo(startCurrent + (dragDelta < 0 ? 1 : -1))
        start()
      } else if (trackRef.current) {
        trackRef.current.style.transform = `translateX(-${startCurrent * 100}%)`
      }
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX
  }
  const handleTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartXRef.current
    if (Math.abs(dx) > 44) {
      stop()
      goTo(current + (dx < 0 ? 1 : -1))
      start()
    }
  }

  return (
    <div id="hcs-root" onMouseEnter={stop} onMouseLeave={start}>
      <div
        id="hcs-clip"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          id="hcs-track"
          ref={trackRef}
          style={{ transform: `translateX(-${current * 100}%)` }}
          className=""
        >
          {HERO_SLIDES.map((slide, i) => (
            <div className="hcs-slide px-2" key={slide.headingBottom}>
              <div className="inline-flex items-center gap-2 sm:gap-3 px-3 py-1.5 sm:px-5 sm:py-2 rounded-full border border-goldPrimary/15 bg-goldPrimary/3 w-fit mx-auto lg:mx-0 backdrop-blur-sm">
                <span className="text-goldPrimary/80 text-sm leading-none select-none">{slide.badgeIcon}</span>
                <span className="block w-px h-3 bg-goldPrimary/18" />
                <span className="text-[9px] sm:text-[10px] uppercase font-semibold tracking-[0.3em] text-goldPrimary/60 font-royal">
                  {slide.badgeLabel}
                </span>
                <span className="block w-px h-3 bg-goldPrimary/18" />
                <span className="text-goldPrimary/35 text-xs leading-none select-none">॥</span>
              </div>

              <div className="space-y-4">
                <h1 className="font-royal leading-[1.12] tracking-[0.03em]">
                  <span className="block text-2xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-5xl font-normal text-[#FBF3E4]">
                    {slide.headingTop}
                  </span>
                  <span className="block text-2xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-5xl font-normal bg-linear-to-r from-goldPrimary via-amber-300 to-goldAccent bg-clip-text text-transparent">
                    {slide.headingBottom}
                  </span>
                </h1>
                <p className="text-sm sm:text-base text-[#FBF3E4]/55 font-light leading-relaxed max-w-lg mx-auto lg:mx-0 pt-1">
                  {slide.copy}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {slide.cards.map((card) => (
                  <div className="hcs-card" key={card.title}>
                    <div className="hcs-card-tl" />
                    <div className="hcs-card-br" />
                    <div className="hcs-icon">
                      <ServiceIcon path={card.icon} />
                    </div>
                    <div className="text-left">
                      <p className="text-[11px] font-bold text-[#FBF3E4] uppercase tracking-wider font-royal leading-none">
                        {card.title}
                      </p>
                      <p className="text-[9px] text-[#E6C878]/55 uppercase tracking-widest mt-1">{card.subtitle}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-2 sm:gap-4">
                <CtaLink
                  cta={slide.primaryCta}
                  className="shine-btn w-full sm:w-auto bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[11px] uppercase tracking-[0.22em] px-10 py-4 rounded-full shadow-xl shadow-goldPrimary/20 transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2.5"
                >
                  {slide.primaryCta.label} <span className="text-base leading-none">→</span>
                </CtaLink>
                <CtaLink
                  cta={slide.secondaryCta}
                  className="w-full sm:w-auto border border-[#E6C878]/40 hover:border-[#E6C878] text-[#FBF3E4] hover:text-[#E6C878] font-semibold text-[11px] text-center uppercase tracking-[0.22em] px-10 py-4 rounded-full transition-all duration-300 hover:bg-[#E6C878]/10"
                >
                  {slide.secondaryCta.label}
                </CtaLink>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
